class PatternConfig extends HTMLElement {
  connectedCallback() {
    const preview = document.getElementById(this.dataset.previewId);
    const error = document.getElementById(this.dataset.errorId);
    const Renderer = typeof jsRender === 'undefined' ? null : jsRender;

    if (!preview || !error || !Renderer) return;

    const fields = {
      targets: this.querySelectorAll('[data-pattern-target]'),
      width: this.querySelector('[data-pattern-width]'),
      height: this.querySelector('[data-pattern-height]'),
      dictionary: this.querySelector('[data-pattern-dictionary-input]'),
      dictionaryGroup: this.querySelector('[data-pattern-dictionary]'),
      rows: this.querySelector('[data-pattern-rows]'),
      columns: this.querySelector('[data-pattern-columns]'),
      spacing: this.querySelector('[data-pattern-spacing]'),
      spacingLabel: this.querySelector('[data-pattern-spacing-label]'),
      diameter: this.querySelector('[data-pattern-diameter-input]'),
      diameterGroup: this.querySelector('[data-pattern-diameter]'),
      id: this.querySelector('[data-pattern-id]'),
      idGroup: this.querySelector('[data-pattern-id-group]'),
      legacyCharuco: this.querySelector('[data-pattern-legacy-charuco-input]'),
      legacyCharucoGroup: this.querySelector('[data-pattern-legacy-charuco]'),
      legacyCharucoProperty: this.querySelector('[data-pattern-legacy-charuco-property]'),
      patternPdfUrl: this.querySelector('[data-pattern-pdf-url]'),
      downloadPdf:
        this.querySelector('[data-pattern-download-pdf]') ||
        document.querySelector(`[data-pattern-download-pdf][data-pattern-config-id="${this.id}"]`),
    };
    const selectedTarget = () => {
      const [firstTarget] = fields.targets;
      return firstTarget?.type === 'radio'
        ? [...fields.targets].find((target) => target.checked)
        : firstTarget;
    };
      const targetType = () => selectedTarget().dataset.patternType || selectedTarget().value;
    const patternParameters = new URLSearchParams(window.location.search);
    const setFieldValue = (field, name) => {
      if (field && patternParameters.has(name)) field.value = patternParameters.get(name);
    };
    const applyPatternParameters = () => {
      if (!fields.width || patternParameters.get('pattern') !== 'pdf') return false;

      const requestedTarget = patternParameters.get('target');
      const target = [...fields.targets].find(
        (input) =>
          input.type === 'radio'
            ? (input.dataset.patternType || input.value) === requestedTarget
            : [...input.options].some((option) => option.value === requestedTarget)
      );
      if (target) {
        if (target.type === 'radio') target.checked = true;
        else target.value = requestedTarget;
      }
      setFieldValue(fields.width, 'width');
      setFieldValue(fields.height, 'height');
      setFieldValue(fields.dictionary, 'dictionary');
      setFieldValue(fields.rows, 'rows');
      setFieldValue(fields.columns, 'columns');
      setFieldValue(fields.spacing, 'spacing');
      setFieldValue(fields.diameter, 'diameter');
      setFieldValue(fields.id, 'id');
      if (fields.legacyCharuco && patternParameters.has('legacy')) {
        fields.legacyCharuco.checked = patternParameters.get('legacy') === 'true';
      }

      return true;
    };
    const boardDimensions = () => {
      if (fields.width && fields.height) {
        return {
          width: Number(fields.width.value || 200),
          height: Number(fields.height.value || 150),
        };
      }

      const productInfo = this.closest('product-info');
      const sizeControl = [...(productInfo?.querySelectorAll('variant-selects input:checked, variant-selects select') || [])].find(
        (control) => /\bsize\b/i.test(control.dataset.optionName || '')
      );
      const size = sizeControl?.value.match(/^(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)$/i);

      return size ? { width: Number(size[1]), height: Number(size[2]) } : { width: 200, height: 150 };
    };
    const updatePreviewRadius = ({ width, height }) => {
      const maximumRadius = Number(this.dataset.previewRadius);
      if (!maximumRadius) return;

      const radius = Math.min(maximumRadius, width / 2, height / 2);
      preview.style.setProperty('--pattern-preview-radius', `${(radius / width) * 100}% / ${(radius / height) * 100}%`);
    };
    const updatePatternPdfUrl = (dimensions) => {
      if (!fields.patternPdfUrl || !this.dataset.generatorUrl) return;

      const url = new URL(this.dataset.generatorUrl, window.location.origin);
      url.search = new URLSearchParams({
        pattern: 'pdf',
        target: targetType(),
        width: dimensions.width,
        height: dimensions.height,
        dictionary: fields.dictionary.value,
        rows: fields.rows.value,
        columns: fields.columns.value,
        spacing: fields.spacing.value,
        diameter: fields.diameter.value,
        id: fields.id?.value || 0,
        legacy: String(fields.legacyCharuco.checked),
      }).toString();
      fields.patternPdfUrl.value = url.toString();
    };
    const addToCart = selectedTarget()?.form?.querySelector('[name="add"]');
    const downloadFromParameters = applyPatternParameters();
    const dimensions = boardDimensions();

    this.renderer = new Renderer(
      `#${preview.id}`,
      'CHECKERBOARD',
      dimensions.width,
      dimensions.height,
      Number(fields.rows.value),
      Number(fields.columns.value),
      Number(fields.spacing.value),
      Number(fields.diameter.value),
      fields.dictionary.value,
      Number(fields.id?.value || 0),
      Math.floor(0.75 * Number(fields.spacing.value)),
      false,
      false,
      false,
      'black_on_white',
      true,
      false,
      fields.legacyCharuco.checked,
      this.dataset.fontUrl
    );

    const rendererType = {
      checker: 'CHECKERBOARD',
      charuco: 'CHARUCO',
      kalibr: 'KALIBR',
      circles: 'CIRCLES',
      circlesA: 'CIRCLES_ASYMMETRIC',
    };

    const updateFields = () => {
      const type = targetType();
      const circles = type.includes('circles');
      const charuco = type === 'charuco';
      const kalibr = type === 'kalibr';

      fields.diameterGroup.hidden = !circles;
      fields.diameter.disabled = !circles;
      fields.diameter.name = circles ? 'properties[Circle Diameter (mm)]' : '';
      fields.dictionaryGroup.hidden = !charuco;
      fields.dictionary.disabled = !charuco;
      fields.dictionary.name = charuco ? 'properties[Dictionary]' : '';
      fields.legacyCharucoGroup.hidden = !charuco;
      fields.legacyCharuco.disabled = !charuco;
      fields.legacyCharuco.name = charuco ? 'properties[ChArUco Legacy]' : '';
      if (fields.legacyCharucoProperty) {
        fields.legacyCharucoProperty.name = charuco ? 'properties[ChArUco Legacy]' : '';
        fields.legacyCharucoProperty.disabled = !charuco || fields.legacyCharuco.checked;
      }
      if (fields.idGroup && fields.id) {
        fields.idGroup.hidden = !charuco && !kalibr;
        fields.id.disabled = !charuco && !kalibr;
      }

      if (kalibr) {
        fields.spacingLabel.textContent = 'Tag Size [mm]';
        fields.spacing.name = 'properties[Tag Size (mm)]';
      } else if (circles) {
        const label = type === 'circles' ? 'Circle Spacing' : 'Diagonal Spacing';
        fields.spacingLabel.textContent = `${label} [mm]`;
        fields.spacing.name = `properties[${label} (mm)]`;
      } else {
        fields.spacingLabel.textContent = 'Checker Width [mm]';
        fields.spacing.name = 'properties[Checker Width (mm)]';
      }
    };

    const render = () => {
      const dimensions = boardDimensions();
      updatePreviewRadius(dimensions);
      updatePatternPdfUrl(dimensions);
      if (fields.legacyCharucoProperty) {
        fields.legacyCharucoProperty.disabled = targetType() !== 'charuco' || fields.legacyCharuco.checked;
      }
      this.renderer.updateOptions({
        type: rendererType[targetType()],
        width: dimensions.width,
        height: dimensions.height,
        rows: Number(fields.rows.value),
        cols: Number(fields.columns.value),
        spacing: Number(fields.spacing.value),
        diameter: Number(fields.diameter.value),
        dict: fields.dictionary.value,
        id: Number(fields.id?.value || 0),
        markerSize: Math.floor(0.75 * Number(fields.spacing.value)),
        legacyCharuco: fields.legacyCharuco.checked,
      });
      this.renderer.draw();

      const exceedsBoard = this.renderer.patternSize.w > this.renderer.width || this.renderer.patternSize.h > this.renderer.height;
      error.hidden = !exceedsBoard;
      error.textContent = exceedsBoard ? 'The pattern exceeds the board dimensions.' : '';

      if (addToCart) {
        addToCart.disabled = exceedsBoard;
        addToCart.setAttribute('aria-disabled', String(exceedsBoard));
      }
    };

    fields.targets.forEach((target) => {
      target.addEventListener('change', () => {
        updateFields();
        render();
      });
    });

    this.variantSelectionUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data: { event } }) => {
      if (event.target.closest('product-info') === this.closest('product-info')) render();
    });

    [fields.width, fields.height, fields.dictionary, fields.rows, fields.columns, fields.spacing, fields.diameter, fields.id, fields.legacyCharuco]
      .filter(Boolean)
      .forEach((field) => {
      field.addEventListener('input', render);
      field.addEventListener('change', render);
      });

    fields.downloadPdf?.addEventListener('click', () => this.renderer.downloadPDF());

    updateFields();
    render();
    if (downloadFromParameters) this.renderer.downloadPDF();
  }

  disconnectedCallback() {
    this.variantSelectionUnsubscriber?.();
  }
}

if (!customElements.get('pattern-config')) {
  customElements.define('pattern-config', PatternConfig);
}